import type {
  Board,
  CastlingRights,
  Color,
  GameState,
  GameStatus,
  Move,
  MoveRecord,
  MoveResult,
  Piece,
  PieceKind,
  PromotionKind,
  ResurrectionEvent,
} from './types';

export const BOARD_SIZE = 8;
export const BOARD_CELLS = BOARD_SIZE * BOARD_SIZE;
export const INITIAL_RNG_STATE = 0x9e3779b9;

type InternalMove = {
  from: number;
  to: number;
  promotion?: PromotionKind;
  enPassant?: boolean;
  castle?: boolean;
};

interface AppliedMove {
  board: Board;
  captured?: Piece;
  resurrected?: ResurrectionEvent;
  castling: CastlingRights;
  enPassant: number | null;
  halfmoveClock: number;
  fullmoveNumber: number;
  rngState: number;
  nextPieceId: number;
}

const FILES = 'abcdefgh';
const KNIGHT_STEPS = [
  [-2, -1], [-2, 1], [-1, -2], [-1, 2],
  [1, -2], [1, 2], [2, -1], [2, 1],
] as const;
const KING_STEPS = [
  [-1, -1], [-1, 0], [-1, 1], [0, -1],
  [0, 1], [1, -1], [1, 0], [1, 1],
] as const;
const BISHOP_RAYS = [[-1, -1], [-1, 1], [1, -1], [1, 1]] as const;
const ROOK_RAYS = [[-1, 0], [1, 0], [0, -1], [0, 1]] as const;
const QUEEN_RAYS = [...BISHOP_RAYS, ...ROOK_RAYS] as const;

export const CAPTURE_VALUES: Record<PieceKind, number> = {
  pawn: 1,
  knight: 3,
  bishop: 3,
  rook: 5,
  queen: 10,
  king: 0,
};

export function opposite(color: Color): Color {
  return color === 'w' ? 'b' : 'w';
}

export function fileOf(index: number): number {
  return index % BOARD_SIZE;
}

export function rankOf(index: number): number {
  return Math.floor(index / BOARD_SIZE);
}

export function boardIndex(file: number, rank: number): number {
  return rank * BOARD_SIZE + file;
}

export function squareToIndex(square: string): number | null {
  if (!/^[a-h][1-8]$/.test(square)) return null;
  const file = FILES.indexOf(square[0]!);
  const rank = Number(square[1]) - 1;
  return boardIndex(file, rank);
}

export function indexToSquare(index: number): string {
  const file = FILES[fileOf(index)] ?? 'a';
  return `${file}${rankOf(index) + 1}`;
}

function inBounds(file: number, rank: number): boolean {
  return file >= 0 && file < BOARD_SIZE && rank >= 0 && rank < BOARD_SIZE;
}

function clonePiece(piece: Piece | null | undefined): Piece | null {
  return piece ? { ...piece } : null;
}

function cloneBoard(board: Board): Board {
  return board.map((piece) => clonePiece(piece));
}

function cloneResurrection(event: ResurrectionEvent | null | undefined): ResurrectionEvent | undefined {
  return event ? { ...event, piece: { ...event.piece } } : undefined;
}

function cloneMoveRecord(record: MoveRecord | null): MoveRecord | null {
  return record ? { ...record, resurrected: cloneResurrection(record.resurrected) } : null;
}

function nonNegativeInteger(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}

export function captureValue(piece: Piece): number {
  return CAPTURE_VALUES[piece.kind];
}

export function cloneState(state: GameState): GameState {
  return {
    ...state,
    board: cloneBoard(state.board),
    castling: { ...state.castling },
    whiteScore: nonNegativeInteger(state.whiteScore),
    blackScore: nonNegativeInteger(state.blackScore),
    whiteCaptureStreak: nonNegativeInteger(state.whiteCaptureStreak),
    blackCaptureStreak: nonNegativeInteger(state.blackCaptureStreak),
    moveHistory: state.moveHistory.map((record) => ({
      ...record,
      resurrected: cloneResurrection(record.resurrected),
    })),
    positionCounts: { ...state.positionCounts },
    lastMove: cloneMoveRecord(state.lastMove),
    lastResurrection: cloneResurrection(state.lastResurrection) ?? null,
  };
}

function createPiece(id: number, color: Color, kind: PieceKind): Piece {
  return { id, color, kind, promoted: false, resurrected: false, hasMoved: false };
}

export function createInitialState(seed = INITIAL_RNG_STATE): GameState {
  const board: Board = Array.from({ length: BOARD_CELLS }, () => null);
  let nextPieceId = 1;
  const backRank: PieceKind[] = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook'];
  for (let file = 0; file < BOARD_SIZE; file += 1) {
    board[boardIndex(file, 0)] = createPiece(nextPieceId++, 'w', backRank[file]!);
    board[boardIndex(file, 1)] = createPiece(nextPieceId++, 'w', 'pawn');
    board[boardIndex(file, 6)] = createPiece(nextPieceId++, 'b', 'pawn');
    board[boardIndex(file, 7)] = createPiece(nextPieceId++, 'b', backRank[file]!);
  }
  const state: GameState = {
    board,
    turn: 'w',
    castling: {
      whiteKingSide: true,
      whiteQueenSide: true,
      blackKingSide: true,
      blackQueenSide: true,
    },
    enPassant: null,
    halfmoveClock: 0,
    fullmoveNumber: 1,
    rngState: normalizeSeed(seed),
    nextPieceId,
    whiteScore: 0,
    blackScore: 0,
    whiteCaptureStreak: 0,
    blackCaptureStreak: 0,
    moveHistory: [],
    positionCounts: {},
    status: 'playing',
    winner: null,
    lastMove: null,
    lastResurrection: null,
    paused: false,
  };
  state.positionCounts[positionKey(state)] = 1;
  return state;
}

function normalizeSeed(seed: number): number {
  const normalized = seed >>> 0;
  return normalized || INITIAL_RNG_STATE;
}

function nextRandom(seed: number): { state: number; value: number } {
  let value = normalizeSeed(seed);
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  value >>>= 0;
  value = normalizeSeed(value);
  return { state: value, value: value / 0x1_0000_0000 };
}

function pieceAttacksSquare(board: Board, source: number, target: number): boolean {
  const piece = board[source];
  if (!piece) return false;
  const sourceFile = fileOf(source);
  const sourceRank = rankOf(source);
  const targetFile = fileOf(target);
  const targetRank = rankOf(target);
  const fileDelta = targetFile - sourceFile;
  const rankDelta = targetRank - sourceRank;

  if (piece.kind === 'pawn') {
    const direction = piece.color === 'w' ? 1 : -1;
    return rankDelta === direction && Math.abs(fileDelta) === 1;
  }
  if (piece.kind === 'knight') {
    return KNIGHT_STEPS.some(([fileStep, rankStep]) => fileDelta === fileStep && rankDelta === rankStep);
  }
  if (piece.kind === 'king') {
    return Math.max(Math.abs(fileDelta), Math.abs(rankDelta)) === 1;
  }

  const rays = piece.kind === 'bishop'
    ? BISHOP_RAYS
    : piece.kind === 'rook'
      ? ROOK_RAYS
      : QUEEN_RAYS;
  for (const [fileStep, rankStep] of rays) {
    let file = sourceFile + fileStep;
    let rank = sourceRank + rankStep;
    while (inBounds(file, rank)) {
      const current = boardIndex(file, rank);
      if (current === target) return true;
      if (board[current]) break;
      file += fileStep;
      rank += rankStep;
    }
  }
  return false;
}

export function isSquareAttacked(board: Board, target: number, byColor: Color): boolean {
  for (let source = 0; source < BOARD_CELLS; source += 1) {
    const piece = board[source];
    if (piece?.color === byColor && pieceAttacksSquare(board, source, target)) return true;
  }
  return false;
}

function findKing(board: Board, color: Color): number {
  return board.findIndex((piece) => piece?.color === color && piece.kind === 'king');
}

export function isInCheck(state: GameState, color: Color): boolean {
  return isInCheckOnBoard(state.board, color);
}

function isInCheckOnBoard(board: Board, color: Color): boolean {
  const king = findKing(board, color);
  return king < 0 || isSquareAttacked(board, king, opposite(color));
}

function addMove(moves: InternalMove[], move: InternalMove): void {
  moves.push(move);
}

function addSlidingMoves(board: Board, source: number, rays: readonly (readonly [number, number])[], moves: InternalMove[]): void {
  const piece = board[source];
  if (!piece) return;
  const sourceFile = fileOf(source);
  const sourceRank = rankOf(source);
  for (const [fileStep, rankStep] of rays) {
    let file = sourceFile + fileStep;
    let rank = sourceRank + rankStep;
    while (inBounds(file, rank)) {
      const target = boardIndex(file, rank);
      const occupant = board[target];
      if (!occupant) addMove(moves, { from: source, to: target });
      else {
        if (occupant.color !== piece.color && occupant.kind !== 'king') {
          addMove(moves, { from: source, to: target });
        }
        break;
      }
      file += fileStep;
      rank += rankStep;
    }
  }
}

function canCastle(state: GameState, color: Color, kingSide: boolean): boolean {
  const rank = color === 'w' ? 0 : 7;
  const kingSource = boardIndex(4, rank);
  const rookSource = boardIndex(kingSide ? 7 : 0, rank);
  const king = state.board[kingSource];
  const rook = state.board[rookSource];
  const rights = color === 'w'
    ? (kingSide ? state.castling.whiteKingSide : state.castling.whiteQueenSide)
    : (kingSide ? state.castling.blackKingSide : state.castling.blackQueenSide);
  if (!rights || !king || king.color !== color || king.kind !== 'king' || !rook || rook.color !== color || rook.kind !== 'rook') {
    return false;
  }
  const betweenFiles = kingSide ? [5, 6] : [1, 2, 3];
  if (betweenFiles.some((file) => state.board[boardIndex(file, rank)])) return false;
  if (isInCheckOnBoard(state.board, color)) return false;
  const transitFiles = kingSide ? [5, 6] : [3, 2];
  return transitFiles.every((file) => !isSquareAttacked(state.board, boardIndex(file, rank), opposite(color)));
}

function generatePseudoMoves(state: GameState, color: Color): InternalMove[] {
  const moves: InternalMove[] = [];
  for (let source = 0; source < BOARD_CELLS; source += 1) {
    const piece = state.board[source];
    if (!piece || piece.color !== color) continue;
    const sourceFile = fileOf(source);
    const sourceRank = rankOf(source);

    if (piece.kind === 'pawn') {
      const direction = color === 'w' ? 1 : -1;
      const startRank = color === 'w' ? 1 : 6;
      const promotionRank = color === 'w' ? 7 : 0;
      const nextRank = sourceRank + direction;
      if (inBounds(sourceFile, nextRank)) {
        const forward = boardIndex(sourceFile, nextRank);
        if (!state.board[forward]) {
          if (nextRank === promotionRank) {
            for (const promotion of ['queen', 'rook', 'bishop', 'knight'] as PromotionKind[]) {
              addMove(moves, { from: source, to: forward, promotion });
            }
          } else {
            addMove(moves, { from: source, to: forward });
          }
          const doubleRank = sourceRank + direction * 2;
          if (sourceRank === startRank && !piece.hasMoved && inBounds(sourceFile, doubleRank)) {
            const doubleForward = boardIndex(sourceFile, doubleRank);
            if (!state.board[doubleForward]) addMove(moves, { from: source, to: doubleForward });
          }
        }
        for (const fileStep of [-1, 1]) {
          const targetFile = sourceFile + fileStep;
          if (!inBounds(targetFile, nextRank)) continue;
          const target = boardIndex(targetFile, nextRank);
          const occupant = state.board[target];
          if (occupant && occupant.color !== color && occupant.kind !== 'king') {
            if (nextRank === promotionRank) {
              for (const promotion of ['queen', 'rook', 'bishop', 'knight'] as PromotionKind[]) {
                addMove(moves, { from: source, to: target, promotion });
              }
            } else {
              addMove(moves, { from: source, to: target });
            }
          } else if (state.enPassant === target && !occupant) {
            const capturedIndex = boardIndex(targetFile, sourceRank);
            const captured = state.board[capturedIndex];
            if (captured?.color === opposite(color) && captured.kind === 'pawn') {
              addMove(moves, { from: source, to: target, enPassant: true });
            }
          }
        }
      }
      continue;
    }

    if (piece.kind === 'knight') {
      for (const [fileStep, rankStep] of KNIGHT_STEPS) {
        const targetFile = sourceFile + fileStep;
        const targetRank = sourceRank + rankStep;
        if (!inBounds(targetFile, targetRank)) continue;
        const target = boardIndex(targetFile, targetRank);
        const occupant = state.board[target];
        if (!occupant || (occupant.color !== color && occupant.kind !== 'king')) addMove(moves, { from: source, to: target });
      }
      continue;
    }

    if (piece.kind === 'bishop') {
      addSlidingMoves(state.board, source, BISHOP_RAYS, moves);
      continue;
    }
    if (piece.kind === 'rook') {
      addSlidingMoves(state.board, source, ROOK_RAYS, moves);
      continue;
    }
    if (piece.kind === 'queen') {
      addSlidingMoves(state.board, source, QUEEN_RAYS, moves);
      continue;
    }

    for (const [fileStep, rankStep] of KING_STEPS) {
      const targetFile = sourceFile + fileStep;
      const targetRank = sourceRank + rankStep;
      if (!inBounds(targetFile, targetRank)) continue;
      const target = boardIndex(targetFile, targetRank);
      const occupant = state.board[target];
      if (!occupant || (occupant.color !== color && occupant.kind !== 'king')) addMove(moves, { from: source, to: target });
    }
    if (canCastle(state, color, true)) addMove(moves, { from: source, to: boardIndex(6, sourceRank), castle: true });
    if (canCastle(state, color, false)) addMove(moves, { from: source, to: boardIndex(2, sourceRank), castle: true });
  }
  return moves;
}

function revokeRookRight(castling: CastlingRights, index: number): void {
  if (index === boardIndex(0, 0)) castling.whiteQueenSide = false;
  if (index === boardIndex(7, 0)) castling.whiteKingSide = false;
  if (index === boardIndex(0, 7)) castling.blackQueenSide = false;
  if (index === boardIndex(7, 7)) castling.blackKingSide = false;
}

function resurrectionRanks(color: Color, kind: PieceKind): number[] {
  if (kind === 'pawn') return [color === 'w' ? 1 : 6];
  return color === 'w' ? [0, 1] : [7, 6];
}

export function getResurrectionSquares(board: Board, captured: Piece, moverColor: Color): string[] {
  const squares: string[] = [];
  for (const rank of resurrectionRanks(captured.color, captured.promoted ? 'pawn' : captured.kind)) {
    for (let file = 0; file < BOARD_SIZE; file += 1) {
      const square = boardIndex(file, rank);
      if (board[square]) continue;
      const piece: Piece = {
        id: 0,
        color: captured.color,
        kind: captured.promoted ? 'pawn' : captured.kind,
        promoted: false,
        resurrected: true,
        hasMoved: false,
      };
      board[square] = piece;
      const safe = !isInCheckOnBoard(board, moverColor);
      board[square] = null;
      if (safe) squares.push(indexToSquare(square));
    }
  }
  return squares;
}

function applyMoveUnchecked(state: GameState, move: InternalMove): AppliedMove | null {
  const moving = state.board[move.from];
  if (!moving || moving.color !== state.turn) return null;
  const board = cloneBoard(state.board);
  const target = board[move.to];
  if (target?.color === moving.color || target?.kind === 'king') return null;

  let capturedIndex: number | null = target ? move.to : null;
  if (move.enPassant) {
    const capturedRank = rankOf(move.to) + (moving.color === 'w' ? -1 : 1);
    capturedIndex = boardIndex(fileOf(move.to), capturedRank);
    const enPassantPiece = board[capturedIndex];
    if (!enPassantPiece || enPassantPiece.color === moving.color || enPassantPiece.kind !== 'pawn') return null;
  }
  const captured = capturedIndex === null ? undefined : board[capturedIndex] ?? undefined;
  if (captured?.kind === 'king') return null;
  if (capturedIndex !== null) board[capturedIndex] = null;

  const movedPiece: Piece = { ...moving, hasMoved: true };
  if (move.promotion) {
    if (moving.kind !== 'pawn') return null;
    movedPiece.kind = move.promotion;
    movedPiece.promoted = true;
  }
  board[move.from] = null;
  board[move.to] = movedPiece;

  if (move.castle) {
    const rank = rankOf(move.from);
    const kingSide = fileOf(move.to) > fileOf(move.from);
    const rookFrom = boardIndex(kingSide ? 7 : 0, rank);
    const rookTo = boardIndex(kingSide ? 5 : 3, rank);
    const rook = board[rookFrom];
    if (!rook || rook.color !== moving.color || rook.kind !== 'rook') return null;
    board[rookFrom] = null;
    board[rookTo] = { ...rook, hasMoved: true };
  }

  const castling = { ...state.castling };
  if (moving.kind === 'king') {
    if (moving.color === 'w') {
      castling.whiteKingSide = false;
      castling.whiteQueenSide = false;
    } else {
      castling.blackKingSide = false;
      castling.blackQueenSide = false;
    }
  }
  if (moving.kind === 'rook') revokeRookRight(castling, move.from);
  if (captured?.kind === 'rook' && capturedIndex !== null) revokeRookRight(castling, capturedIndex);

  const movedTwoSquares = moving.kind === 'pawn' && Math.abs(rankOf(move.to) - rankOf(move.from)) === 2;
  const enPassant = movedTwoSquares
    ? boardIndex(fileOf(move.from), (rankOf(move.from) + rankOf(move.to)) / 2)
    : null;

  let rngState = state.rngState;
  let nextPieceId = state.nextPieceId;
  let resurrected: ResurrectionEvent | undefined;
  if (captured) {
    const spawnKind = captured.promoted ? 'pawn' : captured.kind;
    const candidateSquares = getResurrectionSquares(board, captured, moving.color);
    if (!candidateSquares.length) return null;
    const random = nextRandom(rngState);
    rngState = random.state;
    const selectedSquare = candidateSquares[Math.floor(random.value * candidateSquares.length)]!;
    const selectedIndex = squareToIndex(selectedSquare)!;
    const piece: Piece = {
      id: nextPieceId++,
      color: captured.color,
      kind: spawnKind,
      promoted: false,
      resurrected: true,
      hasMoved: false,
    };
    board[selectedIndex] = piece;
    resurrected = { piece: { ...piece }, square: selectedSquare, originalKind: captured.kind };
  }

  return {
    board,
    captured: captured ? { ...captured } : undefined,
    resurrected,
    castling,
    enPassant,
    halfmoveClock: captured || moving.kind === 'pawn' ? 0 : state.halfmoveClock + 1,
    fullmoveNumber: moving.color === 'b' ? state.fullmoveNumber + 1 : state.fullmoveNumber,
    rngState,
    nextPieceId,
  };
}

function toPublicMove(move: InternalMove): Move {
  return {
    from: indexToSquare(move.from),
    to: indexToSquare(move.to),
    ...(move.promotion ? { promotion: move.promotion } : {}),
  };
}

function matchesMove(internal: InternalMove, requested: Move): boolean {
  return internal.from === squareToIndex(requested.from)
    && internal.to === squareToIndex(requested.to)
    && internal.promotion === requested.promotion;
}

function legalInternalMoves(state: GameState): InternalMove[] {
  return generatePseudoMoves(state, state.turn).filter((move) => {
    const applied = applyMoveUnchecked(state, move);
    return !!applied && !isInCheckOnBoard(applied.board, state.turn);
  });
}

export function generateLegalMoves(state: GameState): Move[] {
  return legalInternalMoves(state).map(toPublicMove);
}

export function legalMovesFrom(state: GameState, square: string): Move[] {
  return generateLegalMoves(state).filter((move) => move.from === square);
}

function materialDraw(board: Board): boolean {
  const pieces = board.filter((piece): piece is Piece => !!piece && piece.kind !== 'king');
  if (pieces.length === 0) return true;
  if (pieces.length === 1 && ['bishop', 'knight'].includes(pieces[0]!.kind)) return true;
  return false;
}

export function positionKey(state: GameState): string {
  const boardKey = state.board.map((piece) => {
    if (!piece) return '-';
    return `${piece.color}${piece.kind}:${piece.promoted ? 'p' : 'n'}${piece.hasMoved ? 'm' : 'u'}`;
  }).join('');
  const castlingKey = [
    state.castling.whiteKingSide ? 'K' : '-',
    state.castling.whiteQueenSide ? 'Q' : '-',
    state.castling.blackKingSide ? 'k' : '-',
    state.castling.blackQueenSide ? 'q' : '-',
  ].join('');
  return `${boardKey}|${state.turn}|${castlingKey}|${state.enPassant ?? '-'}|${state.rngState}`;
}

function statusFor(state: GameState): { status: GameStatus; winner: Color | null } {
  const moves = legalInternalMoves(state);
  if (!moves.length) {
    return isInCheckOnBoard(state.board, state.turn)
      ? { status: 'checkmate', winner: opposite(state.turn) }
      : { status: 'stalemate', winner: null };
  }
  if ((state.positionCounts[positionKey(state)] ?? 0) >= 3) return { status: 'draw-repetition', winner: null };
  if (state.halfmoveClock >= 100) return { status: 'draw-fifty', winner: null };
  if (materialDraw(state.board)) return { status: 'draw-material', winner: null };
  return isInCheckOnBoard(state.board, state.turn)
    ? { status: 'check', winner: null }
    : { status: 'playing', winner: null };
}

export function isGameOver(state: GameState): boolean {
  return state.status === 'checkmate'
    || state.status === 'stalemate'
    || state.status === 'draw-repetition'
    || state.status === 'draw-fifty'
    || state.status === 'draw-material';
}

function commitAppliedMove(state: GameState, internal: InternalMove, applied: AppliedMove): MoveResult {
  const move = toPublicMove(internal);
  const notation = `${move.from}${applied.captured ? 'x' : '-'}${move.to}${move.promotion ? `=${move.promotion[0]!.toUpperCase()}` : ''}`;
  const didCapture = !!applied.captured;
  const previousStreak = state.turn === 'w' ? state.whiteCaptureStreak : state.blackCaptureStreak;
  const captureStreak = didCapture ? previousStreak + 1 : 0;
  const scoreMultiplier = didCapture ? captureStreak : 0;
  const scoreGain = didCapture ? captureValue(applied.captured!) * scoreMultiplier : 0;
  const record: MoveRecord = {
    ...move,
    color: state.turn,
    capture: didCapture,
    notation,
    ...(didCapture ? { scoreGain, scoreMultiplier, captureStreak } : {}),
    ...(applied.resurrected ? { resurrected: cloneResurrection(applied.resurrected) } : {}),
  };
  const nextState: GameState = {
    ...state,
    board: applied.board,
    turn: opposite(state.turn),
    castling: applied.castling,
    enPassant: applied.enPassant,
    halfmoveClock: applied.halfmoveClock,
    fullmoveNumber: applied.fullmoveNumber,
    rngState: applied.rngState,
    nextPieceId: applied.nextPieceId,
    whiteScore: state.whiteScore + (state.turn === 'w' ? scoreGain : 0),
    blackScore: state.blackScore + (state.turn === 'b' ? scoreGain : 0),
    whiteCaptureStreak: state.turn === 'w' ? captureStreak : state.whiteCaptureStreak,
    blackCaptureStreak: state.turn === 'b' ? captureStreak : state.blackCaptureStreak,
    moveHistory: [...state.moveHistory, record],
    positionCounts: { ...state.positionCounts },
    lastMove: record,
    lastResurrection: cloneResurrection(applied.resurrected) ?? null,
  };
  const key = positionKey(nextState);
  nextState.positionCounts[key] = (nextState.positionCounts[key] ?? 0) + 1;
  const status = statusFor(nextState);
  nextState.status = status.status;
  nextState.winner = status.winner;
  return {
    ok: true,
    state: nextState,
    move,
    ...(applied.captured ? { captured: { ...applied.captured } } : {}),
    ...(applied.resurrected ? { resurrected: cloneResurrection(applied.resurrected) } : {}),
    ...(didCapture ? { scoreGain, scoreMultiplier, captureStreak } : {}),
  };
}

export function applyLegalMove(state: GameState, requested: Move): MoveResult {
  const from = squareToIndex(requested.from);
  const to = squareToIndex(requested.to);
  if (from === null || to === null) return { ok: false, reason: 'INVALID SQUARE' };
  const internal = generatePseudoMoves(state, state.turn).find((move) => matchesMove(move, requested));
  if (!internal) return { ok: false, reason: 'ILLEGAL MOVE' };
  const applied = applyMoveUnchecked(state, internal);
  if (!applied || isInCheckOnBoard(applied.board, state.turn)) return { ok: false, reason: 'ILLEGAL MOVE' };
  return commitAppliedMove(state, internal, applied);
}

export function applyMove(state: GameState, requested: Move): MoveResult {
  const from = squareToIndex(requested.from);
  const to = squareToIndex(requested.to);
  if (from === null || to === null) return { ok: false, reason: 'INVALID SQUARE' };
  const internal = legalInternalMoves(state).find((move) => matchesMove(move, requested));
  if (!internal) {
    const promotionExists = generatePseudoMoves(state, state.turn).some((move) => move.from === from && move.to === to && move.promotion);
    return { ok: false, reason: promotionExists ? 'PROMOTION REQUIRED' : 'ILLEGAL MOVE' };
  }
  const applied = applyMoveUnchecked(state, internal);
  if (!applied || isInCheckOnBoard(applied.board, state.turn)) return { ok: false, reason: 'ILLEGAL MOVE' };
  return commitAppliedMove(state, internal, applied);
}

export function seedFromString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return normalizeSeed(hash);
}
